import { useApiClient } from './apiClient';
import { ApiRoutes } from '@/constants/apiConstants';
import type {
  EmployeeResponse,
  EmployeeRequest,
  WorkScheduleResponse,
  WorkScheduleRequest,
  EmployeeUnavailabilityResponse,
  EmployeeUnavailabilityRequest,
  EmployeeQualificationResponse,
  AbsenceForCalendarResponse
} from '@/types/employee';
import type { EmployeeInvitationResponse, CompleteEmployeeInvitationRequest } from '@/types/employeeInvitation';

export class EmployeeService {
  // Employee Profile CRUD
  static async getEmployees(): Promise<EmployeeResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeResponse[]>(ApiRoutes.Employee.GetEmployees) as unknown as EmployeeResponse[];
    return response;
  }

  static async getEmployeeById(employeeId: number): Promise<EmployeeResponse> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeResponse>(ApiRoutes.Employee.GetEmployeeById, {
        params: { employeeId }
      }) as unknown as EmployeeResponse;
    return response;
  }

  static async createEmployee(request: EmployeeRequest): Promise<EmployeeResponse> {
    const response = await (await useApiClient()).apiClient
      .post<EmployeeResponse>(ApiRoutes.Employee.CreateEmployee, request) as unknown as EmployeeResponse;
    return response;
  }

  static async updateEmployee(employeeId: number, request: EmployeeRequest): Promise<EmployeeResponse> {
    const response = await (await useApiClient()).apiClient
      .put<EmployeeResponse>(ApiRoutes.Employee.UpdateEmployee, request, {
        params: { employeeId }
      }) as unknown as EmployeeResponse;
    return response;
  }

  static async deleteEmployee(employeeId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .delete(ApiRoutes.Employee.DeleteEmployee, {
        params: { employeeId }
      });
  }

  // Unavailability Management
  static async getEmployeeUnavailability(employeeId: number): Promise<EmployeeUnavailabilityResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeUnavailabilityResponse[]>(ApiRoutes.Employee.GetEmployeeUnavailability, {
        params: { employeeId }
      }) as unknown as EmployeeUnavailabilityResponse[];
    return response;
  }

  static async createUnavailability(request: EmployeeUnavailabilityRequest): Promise<EmployeeUnavailabilityResponse> {
    const response = await (await useApiClient()).apiClient
      .post<EmployeeUnavailabilityResponse>(ApiRoutes.Employee.CreateUnavailability, request) as unknown as EmployeeUnavailabilityResponse;
    return response;
  }

  static async deleteUnavailability(unavailabilityId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .delete(ApiRoutes.Employee.DeleteUnavailability, {
        params: { unavailabilityId }
      });
  }

  static async getAbsencesByEmployeeIdAndDateRange(
    employeeId: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<EmployeeUnavailabilityResponse[]> {
    const params: Record<string, string> = { employeeId: employeeId.toString() };
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();

    const response = await (await useApiClient()).apiClient
      .get<EmployeeUnavailabilityResponse[]>(ApiRoutes.Employee.GetAbsencesByEmployeeIdAndDateRange, {
        params
      }) as unknown as EmployeeUnavailabilityResponse[];
    return response;
  }

  static async getAbsencesForCalendar(
    startDate?: Date,
    endDate?: Date
  ): Promise<AbsenceForCalendarResponse[]> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();

    const response = await (await useApiClient()).apiClient
      .get<AbsenceForCalendarResponse[]>(ApiRoutes.Employee.GetAbsencesForCalendar, {
        params
      }) as unknown as AbsenceForCalendarResponse[];
    return response;
  }

  // Work Schedule Management
  static async getEmployeeSchedule(employeeId: number): Promise<WorkScheduleResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<WorkScheduleResponse[]>(ApiRoutes.Employee.GetEmployeeSchedule, {
        params: { employeeId }
      }) as unknown as WorkScheduleResponse[];
    return response;
  }

  static async createSchedule(request: WorkScheduleRequest): Promise<WorkScheduleResponse> {
    const response = await (await useApiClient()).apiClient
      .post<WorkScheduleResponse>(ApiRoutes.Employee.CreateSchedule, request) as unknown as WorkScheduleResponse;
    return response;
  }

  static async updateSchedule(scheduleId: number, request: WorkScheduleRequest): Promise<WorkScheduleResponse> {
    const response = await (await useApiClient()).apiClient
      .put<WorkScheduleResponse>(ApiRoutes.Employee.UpdateSchedule, request, {
        params: { scheduleId }
      }) as unknown as WorkScheduleResponse;
    return response;
  }

  static async deleteSchedule(scheduleId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .delete(ApiRoutes.Employee.DeleteSchedule, {
        params: { scheduleId }
      });
  }

  // Qualification Management
  static async getEmployeeQualifications(employeeId: number): Promise<EmployeeQualificationResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeQualificationResponse[]>(ApiRoutes.Employee.GetEmployeeQualifications, {
        params: { employeeId }
      }) as unknown as EmployeeQualificationResponse[];
    return response;
  }

  static async saveEmployeeQualifications(employeeId: number, qualificationItems: any): Promise<void> {
    await (await useApiClient()).apiClient
      .post(ApiRoutes.Employee.SaveEmployeeQualifications, qualificationItems, {
        params: { employeeId }
      });
  }

  static async getQualifiedEmployeesForService(serviceId: number): Promise<EmployeeResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeResponse[]>(ApiRoutes.Employee.GetQualifiedEmployeesForService, {
        params: { serviceId }
      }) as unknown as EmployeeResponse[];
    return response;
  }

  static async getExpiringQualifications(daysThreshold: number = 30): Promise<EmployeeQualificationResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeQualificationResponse[]>(ApiRoutes.Employee.GetExpiringQualifications, {
        params: { daysThreshold }
      }) as unknown as EmployeeQualificationResponse[];
    return response;
  }

  // My Schedule & Absences (Therapist Self-Management)
  static async getMySchedule(): Promise<WorkScheduleResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<WorkScheduleResponse[]>(ApiRoutes.Employee.GetMySchedule) as unknown as WorkScheduleResponse[];
    return response;
  }

  static async createMySchedule(request: WorkScheduleRequest): Promise<WorkScheduleResponse> {
    const response = await (await useApiClient()).apiClient
      .post<WorkScheduleResponse>(ApiRoutes.Employee.CreateMySchedule, request) as unknown as WorkScheduleResponse;
    return response;
  }

  static async updateMySchedule(scheduleId: number, request: WorkScheduleRequest): Promise<void> {
    await (await useApiClient()).apiClient
      .put(ApiRoutes.Employee.UpdateMySchedule, request, {
        params: { scheduleId }
      });
  }

  static async deleteMySchedule(scheduleId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .delete(ApiRoutes.Employee.DeleteMySchedule, {
        params: { scheduleId }
      });
  }

  static async getMyAbsences(): Promise<EmployeeUnavailabilityResponse[]> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeUnavailabilityResponse[]>(ApiRoutes.Employee.GetMyAbsences) as unknown as EmployeeUnavailabilityResponse[];
    return response;
  }

  static async createMyAbsence(request: EmployeeUnavailabilityRequest): Promise<EmployeeUnavailabilityResponse> {
    const response = await (await useApiClient()).apiClient
      .post<EmployeeUnavailabilityResponse>(ApiRoutes.Employee.CreateMyAbsence, request) as unknown as EmployeeUnavailabilityResponse;
    return response;
  }

  static async updateMyAbsence(absenceId: number, request: EmployeeUnavailabilityRequest): Promise<void> {
    await (await useApiClient()).apiClient
      .put(ApiRoutes.Employee.UpdateMyAbsence, request, {
        params: { absenceId }
      });
  }

  static async deleteMyAbsence(absenceId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .delete(ApiRoutes.Employee.DeleteMyAbsence, {
        params: { absenceId }
      });
  }

  // Employee Invitation
  static async sendInvitation(employeeId: number): Promise<void> {
    await (await useApiClient()).apiClient
      .post(ApiRoutes.Employee.SendInvitation, { employeeId });
  }

  static async validateInvitationToken(token: string): Promise<boolean> {
    const response = await (await useApiClient()).apiClient
      .get<boolean>(`${ApiRoutes.Employee.ValidateInvitationToken}?token=${encodeURIComponent(token)}`) as unknown as boolean;
    return response;
  }

  static async getInvitationByToken(token: string): Promise<EmployeeInvitationResponse> {
    const response = await (await useApiClient()).apiClient
      .get<EmployeeInvitationResponse>(`${ApiRoutes.Employee.GetInvitationByToken}?token=${encodeURIComponent(token)}`) as unknown as EmployeeInvitationResponse;
    return response;
  }

  static async completeInvitation(request: CompleteEmployeeInvitationRequest): Promise<EmployeeInvitationResponse> {
    const response = await (await useApiClient()).apiClient
      .post<EmployeeInvitationResponse>(ApiRoutes.Employee.CompleteInvitation, request) as unknown as EmployeeInvitationResponse;
    return response;
  }
}
